import { CSSProperties, useEffect, useRef, useState } from "react";
import {
    ArrowClockwise, ChatSquareQuote, CheckCircleFill, CheckLg, Display, ExclamationTriangleFill, HouseDoor,
    Person, Phone, Stars, Trash3, XLg,
} from "react-bootstrap-icons";
import { GetHasEditAccess, GetNewsletterServiceClient } from "../App";
import {
    ListNewslettersForReviewRequest,
    NewsletterReviewItem,
    RegenerateNewsletterRequest,
    ReviewNewsletterRequest,
} from "../protos/adminbackendproto/AdminNewsletterService_pb";
import ConfirmationDialog from "./ConfirmationDialog";

interface PassageComment {
    quote: string;
    note: string;
}

type PreviewMode = "desktop" | "mobile";
const PREVIEW_WIDTHS: { [mode in PreviewMode]: number } = { desktop: 680, mobile: 390 };
const HIGHLIGHT_NAME = "review-comments";

function relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "";
    const minutes = Math.round((Date.now() - then) / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

// Finds a selected quote in the letter again (after the preview reloads, or to
// mark it once commented). Whitespace is collapsed on both sides because
// Selection.toString() and the DOM's text nodes disagree on line breaks.
function findQuoteRange(doc: Document, quote: string): Range | null {
    if (!doc.body) return null;
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const positions: { node: Text, offset: number }[] = [];
    let text = "";
    let lastWasSpace = true;
    for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
        const data = node.data;
        for (let i = 0; i < data.length; i++) {
            if (/\s/.test(data[i])) {
                if (lastWasSpace) continue;
                text += " ";
                lastWasSpace = true;
            } else {
                text += data[i];
                lastWasSpace = false;
            }
            positions.push({ node, offset: i });
        }
    }
    const needle = quote.replace(/\s+/g, " ").trim();
    const start = needle ? text.indexOf(needle) : -1;
    if (start < 0) return null;
    const first = positions[start];
    const last = positions[start + needle.length - 1];
    const range = doc.createRange();
    range.setStart(first.node, first.offset);
    range.setEnd(last.node, last.offset + 1);
    return range;
}

// Marks commented passages inside the preview with the CSS Custom Highlight
// API (no DOM changes, so the letter HTML stays untouched). No-op where the
// browser doesn't support it.
function applyHighlights(iframe: HTMLIFrameElement | null, quotes: string[]) {
    const win = iframe?.contentWindow as any;
    const doc = iframe?.contentDocument;
    if (!win || !doc || !win.CSS?.highlights || !win.Highlight) return;
    try {
        if (!doc.getElementById("review-highlight-style")) {
            const style = doc.createElement("style");
            style.id = "review-highlight-style";
            style.textContent = `::highlight(${HIGHLIGHT_NAME}) { background-color: rgba(224, 180, 92, 0.38); }`;
            doc.head?.appendChild(style);
        }
        const ranges = quotes.map((q) => findQuoteRange(doc, q)).filter((r): r is Range => r !== null);
        win.CSS.highlights.set(HIGHLIGHT_NAME, new win.Highlight(...ranges));
    } catch (err) {
        console.log("Could not highlight comments", err);
    }
}

export default function NewsletterReviewPage() {
    const client = GetNewsletterServiceClient();
    const hasEditAccess = GetHasEditAccess();

    const [newsletters, setNewsletters] = useState<NewsletterReviewItem[]>();
    const [loadError, setLoadError] = useState<string>();
    const [error, setError] = useState<string>();
    const [notice, setNotice] = useState<string>();
    const [refreshing, setRefreshing] = useState(false);
    const [selectedId, setSelectedId] = useState<string>();
    const [previewMode, setPreviewMode] = useState<PreviewMode>("desktop");
    const [previewHeight, setPreviewHeight] = useState(560);
    const [selectionPopover, setSelectionPopover] = useState<{ x: number, y: number, quote: string }>();
    const [pendingAction, setPendingActionState] = useState<string>();
    // Mirrored in a ref so the poll interval (bound once) sees the live value.
    const pendingActionRef = useRef<string | undefined>(undefined);
    function setPendingAction(id: string | undefined) {
        pendingActionRef.current = id;
        setPendingActionState(id);
    }
    const [overallDrafts, setOverallDrafts] = useState<{ [id: string]: string }>({});
    const [comments, setComments] = useState<{ [id: string]: PassageComment[] }>({});
    // At most one comment is being written at a time: the quote was captured
    // from the preview selection and we're waiting for its note.
    const [draftComment, setDraftComment] = useState<{ id: string, quote: string, note: string }>();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const stageRef = useRef<HTMLDivElement | null>(null);
    const detailRef = useRef<HTMLDivElement | null>(null);

    function load(silent: boolean = false) {
        if (!silent) setRefreshing(true);
        client.listNewslettersForReview(new ListNewslettersForReviewRequest(), {})
            .then((response) => {
                setNewsletters(response.getNewslettersList());
                setLoadError(undefined);
            })
            .catch((err) => {
                console.log("Failed to load newsletters", err);
                // Transient poll failures shouldn't error-lock the page.
                if (!silent) setLoadError("Failed to load newsletters for review.");
            })
            .finally(() => { if (!silent) setRefreshing(false) });
    }

    useEffect(() => {
        load();
        // Background poll so queued revisions appear on their own. Paused while
        // an approve/reject/regenerate is in flight to avoid list flicker.
        const timer = setInterval(() => {
            if (!pendingActionRef.current) load(true);
        }, 10_000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep a letter selected whenever there is one.
    useEffect(() => {
        if (!newsletters) return;
        if (!selectedId || !newsletters.some((n) => n.getId() === selectedId)) {
            setSelectedId(newsletters[0]?.getId());
        }
    }, [newsletters, selectedId]);

    useEffect(() => {
        setSelectionPopover(undefined);
        detailRef.current?.scrollTo({ top: 0 });
    }, [selectedId]);

    // J / K moves through the queue when you're not typing.
    const moveRef = useRef<(delta: number) => void>();
    moveRef.current = (delta: number) => {
        if (!newsletters || newsletters.length === 0) return;
        const idx = newsletters.findIndex((n) => n.getId() === selectedId);
        const next = newsletters[Math.min(Math.max(idx + delta, 0), newsletters.length - 1)];
        setSelectedId(next.getId());
    };
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (e.metaKey || e.ctrlKey || e.altKey || target.closest("input, textarea, select, [contenteditable]")) return;
            if (e.key === "j") moveRef.current?.(1);
            if (e.key === "k") moveRef.current?.(-1);
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, []);

    function measurePreview() {
        const doc = iframeRef.current?.contentDocument;
        if (!doc?.documentElement) return;
        setPreviewHeight(Math.max(400, doc.documentElement.scrollHeight));
    }

    // Re-measure after the frame finishes animating to a new width.
    useEffect(() => {
        const timer = setTimeout(measurePreview, 380);
        return () => clearTimeout(timer);
    }, [previewMode]);

    const selectedComments = selectedId ? comments[selectedId] : undefined;
    useEffect(() => {
        applyHighlights(iframeRef.current, (selectedComments || []).map((c) => c.quote));
    }, [selectedComments]);

    function onPreviewLoad() {
        measurePreview();
        const iframe = iframeRef.current;
        const doc = iframe?.contentDocument;
        if (!iframe || !doc) return;
        applyHighlights(iframe, (comments[selectedId || ""] || []).map((c) => c.quote));
        // The preview iframe is same-origin (srcdoc + allow-same-origin, scripts
        // still blocked), so the reviewer's selection inside it is readable here.
        const updatePopover = () => {
            const selection = doc.getSelection();
            const quote = selection?.toString().trim();
            if (!selection || !quote || selection.rangeCount === 0 || !stageRef.current) {
                setSelectionPopover(undefined);
                return;
            }
            const rect = selection.getRangeAt(0).getBoundingClientRect();
            const frameRect = iframe.getBoundingClientRect();
            const stageRect = stageRef.current.getBoundingClientRect();
            setSelectionPopover({
                x: frameRect.left - stageRect.left + rect.left + rect.width / 2,
                y: frameRect.top - stageRect.top + rect.top,
                quote: quote.slice(0, 400),
            });
        };
        doc.addEventListener("mouseup", () => setTimeout(updatePopover, 0));
        doc.addEventListener("keyup", updatePopover);
        doc.addEventListener("mousedown", () => setSelectionPopover(undefined));
    }

    function selectNextAfter(id: string) {
        const current = newsletters || [];
        const idx = current.findIndex((n) => n.getId() === id);
        const remaining = current.filter((n) => n.getId() !== id);
        setNewsletters(remaining);
        setSelectedId(remaining[Math.min(idx, remaining.length - 1)]?.getId());
    }

    async function review(newsletter: NewsletterReviewItem, approve: boolean) {
        const id = newsletter.getId();
        if (!approve && !(await ConfirmationDialog("Reject newsletter",
            `Reject “${newsletter.getSubject()}” for ${newsletter.getHomeownerName() || newsletter.getHomeownerEmail()}? It won't be sent.`,
            { okLabel: "Reject" }))) {
            return;
        }
        setPendingAction(id);
        const request = new ReviewNewsletterRequest();
        request.setNewsletterId(id);
        request.setApprove(approve);
        client.reviewNewsletter(request, {})
            .then(() => {
                selectNextAfter(id);
                setNotice(approve ? "Approved." : "Rejected.");
            })
            .catch((err) => {
                console.log("Failed to review newsletter", err);
                setError("Failed to update newsletter status.");
            })
            .finally(() => setPendingAction(undefined));
    }

    function startComment(id: string, quote?: string) {
        const selection = quote || iframeRef.current?.contentWindow?.getSelection()?.toString().trim();
        if (!selection) {
            setNotice("Select a passage in the letter preview first, then add your comment.");
            return;
        }
        setDraftComment({ id, quote: selection.slice(0, 400), note: "" });
        setSelectionPopover(undefined);
    }

    function saveComment() {
        if (!draftComment || !draftComment.note.trim()) return;
        setComments((current) => ({
            ...current,
            [draftComment.id]: [...(current[draftComment.id] || []),
            { quote: draftComment.quote, note: draftComment.note.trim() }],
        }));
        setDraftComment(undefined);
        iframeRef.current?.contentWindow?.getSelection()?.removeAllRanges();
    }

    function removeComment(id: string, index: number) {
        setComments((current) => ({
            ...current,
            [id]: (current[id] || []).filter((_, i) => i !== index),
        }));
    }

    function composeFeedback(id: string): string {
        const passageComments = comments[id] || [];
        const overall = (overallDrafts[id] || "").trim();
        const parts: string[] = [];
        if (passageComments.length > 0) {
            parts.push("Reviewer comments on specific passages:");
            passageComments.forEach((comment, i) => {
                parts.push(`${i + 1}. Passage: "${comment.quote}"\n   Requested change: ${comment.note}`);
            });
        }
        if (overall) {
            parts.push(`Overall: ${overall}`);
        }
        return parts.join("\n");
    }

    function regenerate(id: string) {
        const feedback = composeFeedback(id);
        if (!feedback) {
            setNotice("Add a passage comment or an overall note before regenerating.");
            return;
        }
        setPendingAction(id);
        const request = new RegenerateNewsletterRequest();
        request.setNewsletterId(id);
        request.setFeedback(feedback);
        client.regenerateNewsletter(request, {})
            .then((response) => {
                if (response.getQueued()) {
                    selectNextAfter(id);
                    setComments((current) => ({ ...current, [id]: [] }));
                    setOverallDrafts((current) => ({ ...current, [id]: "" }));
                    setNotice("Revision queued. The new version shows up in this list in a minute or two.");
                } else {
                    setError("Could not queue the revision (the letter may already be reviewed).");
                }
            })
            .catch((err) => {
                console.log("Failed to regenerate newsletter", err);
                setError("Failed to queue the regeneration.");
            })
            .finally(() => setPendingAction(undefined));
    }

    useEffect(() => {
        if (!notice) return;
        const timer = setTimeout(() => setNotice(undefined), 5000);
        return () => clearTimeout(timer);
    }, [notice]);

    const pendingCount = newsletters?.length ?? 0;
    const selected = newsletters?.find((n) => n.getId() === selectedId);

    return (
        <div className="page">
            <header className="page-header">
                <div className="flex-grow-1">
                    <h1 className="page-title d-flex align-items-center gap-2">
                        Newsletter review
                        {newsletters && <span className={"chip " + (pendingCount > 0 ? "warn" : "accent")}>{pendingCount} pending</span>}
                    </h1>
                    <div className="page-subtitle">Read each weekly letter before it goes out. Approve it, reject it, or send it back with notes.</div>
                </div>
                <button className="btn btn-ghost" onClick={() => load()} disabled={refreshing}>
                    <ArrowClockwise className={refreshing ? "spin" : ""} /> Refresh
                </button>
            </header>

            {(error || notice) && <div className="px-4 pt-3 d-flex flex-column gap-2">
                {error && <div className="banner danger"><ExclamationTriangleFill className="mt-1 flex-shrink-0" /> {error}
                    <button className="icon-btn banner-close" onClick={() => setError(undefined)}><XLg size={12} /></button></div>}
                {notice && <div className="banner info" key={notice}>{notice}
                    <button className="icon-btn banner-close" onClick={() => setNotice(undefined)}><XLg size={12} /></button></div>}
            </div>}

            {loadError && !newsletters
                ? <div className="p-4"><div className="banner danger"><ExclamationTriangleFill className="mt-1" /> {loadError}</div></div>
                : !newsletters
                    ? <div className="split review-split">
                        <div className="pane p-3 d-flex flex-column gap-2">
                            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 76, opacity: 1 - i * 0.15 }} />)}
                        </div>
                        <div className="pane p-4"><div className="skeleton" style={{ height: 480, maxWidth: 680 }} /></div>
                    </div>
                    : newsletters.length === 0
                        ? <div className="empty-state">
                            <div className="empty-icon done"><CheckCircleFill size={22} /></div>
                            <h3>All caught up</h3>
                            <div>No newsletters are waiting for review. New ones appear here automatically.</div>
                        </div>
                        : <div className="split review-split">
                            <div className="pane queue">
                                <div className="queue-list stagger">
                                    {newsletters.map((newsletter, n) => {
                                        const id = newsletter.getId();
                                        const commentCount = (comments[id] || []).length;
                                        return <button key={id} style={{ "--i": n } as CSSProperties}
                                            className={"queue-item" + (id === selectedId ? " active" : "")}
                                            onClick={() => setSelectedId(id)}>
                                            <div className="queue-top">
                                                <span className="text-truncate">{newsletter.getAddressTitle()}</span>
                                                <span className="queue-time">{relativeTime(newsletter.getCreatedAt())}</span>
                                            </div>
                                            <div className="queue-subject">{newsletter.getSubject()}</div>
                                            <div className="queue-bottom">
                                                <span className="text-truncate">{newsletter.getHomeownerName() || newsletter.getHomeownerEmail()}</span>
                                                {newsletter.getAutoReviewVerdict() === "flag" && <span className="chip danger">Flagged</span>}
                                                {newsletter.getVersion() > 1 && <span className="chip info">v{newsletter.getVersion()}</span>}
                                                {commentCount > 0 && <span className="chip warn"><ChatSquareQuote size={10} /> {commentCount}</span>}
                                            </div>
                                        </button>
                                    })}
                                </div>
                                <div className="queue-hint"><span className="kbd">J</span> <span className="kbd">K</span> to move between letters</div>
                            </div>

                            {selected && <div className="pane review-detail" ref={detailRef}>
                                <div className="review-inner" key={selected.getId()}>
                                    <div className="review-head">
                                        <div className="min-w-0">
                                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                                <span className={"chip " + (selected.getVersion() > 1 ? "info" : "")}>Version {selected.getVersion()}</span>
                                                <span className="text-secondary small">Generated {new Date(selected.getCreatedAt()).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                                            </div>
                                            <h2 className="review-subject">{selected.getSubject()}</h2>
                                            {selected.getPreheader() && <div className="review-preheader">{selected.getPreheader()}</div>}
                                            <div className="review-meta">
                                                <span><HouseDoor size={12} /> {selected.getAddressTitle()}, {selected.getAddressSubtitle()}</span>
                                                <span><Person size={12} /> {selected.getHomeownerName()} <span className="text-secondary">{selected.getHomeownerEmail()}</span></span>
                                            </div>
                                        </div>
                                        <div className="review-actions">
                                            <button className="btn btn-danger-ghost" disabled={!hasEditAccess || pendingAction === selected.getId()}
                                                onClick={() => review(selected, false)}><XLg /> Reject</button>
                                            <button className="btn btn-accent" disabled={!hasEditAccess || pendingAction === selected.getId()}
                                                onClick={() => review(selected, true)}>
                                                {pendingAction === selected.getId()
                                                    ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 1.5 }} />
                                                    : <CheckLg />} Approve
                                            </button>
                                        </div>
                                    </div>

                                    {selected.getAutoReviewVerdict() === "flag" && <div className="revision-callout flagged">
                                        <div className="section-title mb-1">Claude flagged this letter</div>
                                        <div className="revision-text">{selected.getAutoReviewReason()}</div>
                                    </div>}

                                    {selected.getRevisionFeedback() && <div className="revision-callout">
                                        <div className="section-title mb-1"><ArrowClockwise /> Revised from your notes</div>
                                        <div className="revision-text">{selected.getRevisionFeedback()}</div>
                                    </div>}

                                    <div className="review-body">
                                        <div className="preview-col">
                                            <div className="preview-toolbar">
                                                <div className="segmented segmented-compact">
                                                    <button className={"segment" + (previewMode === "desktop" ? " active" : "")} onClick={() => setPreviewMode("desktop")}><Display size={12} /> Desktop</button>
                                                    <button className={"segment" + (previewMode === "mobile" ? " active" : "")} onClick={() => setPreviewMode("mobile")}><Phone size={12} /> Mobile</button>
                                                </div>
                                                <span className="text-secondary small">Highlight any text in the letter to comment on it</span>
                                            </div>
                                            <div className="preview-stage" ref={stageRef}>
                                                <div className={"email-frame " + previewMode} style={{ width: PREVIEW_WIDTHS[previewMode] }}>
                                                    <iframe
                                                        key={selected.getId()}
                                                        title={`newsletter-${selected.getId()}`}
                                                        ref={iframeRef}
                                                        srcDoc={selected.getHtml()}
                                                        sandbox="allow-same-origin"
                                                        onLoad={onPreviewLoad}
                                                        style={{ height: previewHeight }}
                                                    />
                                                </div>
                                                {selectionPopover && hasEditAccess && <button
                                                    className="selection-popover"
                                                    style={{ left: selectionPopover.x, top: selectionPopover.y }}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => startComment(selected.getId(), selectionPopover.quote)}>
                                                    <ChatSquareQuote size={13} /> Comment
                                                </button>}
                                            </div>
                                        </div>

                                        <aside className="feedback-col">
                                            <div className="panel feedback-panel">
                                                <div className="section-title">
                                                    Feedback
                                                    {(selectedComments || []).length > 0 && <span className="count">{selectedComments!.length}</span>}
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    {(selectedComments || []).map((comment, index) => (
                                                        <div key={index} className="comment animate-in">
                                                            <div className="comment-quote">“{comment.quote.length > 160
                                                                ? comment.quote.slice(0, 160) + "…" : comment.quote}”</div>
                                                            <div className="comment-note">{comment.note}</div>
                                                            <button className="icon-btn danger comment-remove" title="Remove comment"
                                                                onClick={() => removeComment(selected.getId(), index)}><Trash3 size={12} /></button>
                                                        </div>
                                                    ))}
                                                    {draftComment?.id === selected.getId()
                                                        ? <div className="comment draft animate-in">
                                                            <div className="comment-quote">“{draftComment.quote.length > 160
                                                                ? draftComment.quote.slice(0, 160) + "…" : draftComment.quote}”</div>
                                                            <textarea
                                                                autoFocus
                                                                className="form-control mt-2"
                                                                rows={2}
                                                                placeholder="What should change here?"
                                                                value={draftComment.note}
                                                                onChange={(e) => setDraftComment({ ...draftComment, note: e.target.value })}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveComment(); }
                                                                    if (e.key === "Escape") setDraftComment(undefined);
                                                                }}
                                                            />
                                                            <div className="d-flex justify-content-end gap-2 mt-2">
                                                                <button className="btn btn-ghost btn-sm" onClick={() => setDraftComment(undefined)}>Cancel</button>
                                                                <button className="btn btn-accent btn-sm" onClick={saveComment} disabled={!draftComment.note.trim()}>Add comment</button>
                                                            </div>
                                                        </div>
                                                        : (selectedComments || []).length === 0 && <div className="text-secondary small feedback-empty">
                                                            <ChatSquareQuote className="me-2" />Highlight a passage in the letter, then click <strong>Comment</strong>.
                                                        </div>}
                                                </div>

                                                <label className="field-label mt-3" htmlFor="overall-note">Overall note</label>
                                                <textarea
                                                    id="overall-note"
                                                    className="form-control"
                                                    rows={3}
                                                    placeholder="Optional, e.g. 'a touch shorter throughout'"
                                                    value={overallDrafts[selected.getId()] || ""}
                                                    disabled={!hasEditAccess}
                                                    onChange={(e) => setOverallDrafts({ ...overallDrafts, [selected.getId()]: e.target.value })}
                                                />
                                                <button
                                                    className="btn btn-ghost w-100 mt-3"
                                                    disabled={!hasEditAccess || pendingAction === selected.getId() || !composeFeedback(selected.getId())}
                                                    onClick={() => regenerate(selected.getId())}>
                                                    <Stars /> Regenerate with feedback
                                                </button>
                                                <div className="text-secondary small mt-2">
                                                    Sends your comments back to the writer. The new version replaces this one in the queue.
                                                </div>
                                            </div>
                                        </aside>
                                    </div>
                                </div>
                            </div>}
                        </div>}
        </div>
    );
}
